import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

interface PopoverPosition {
  top: number;
  left: number;
  minWidth: number;
}

export function usePopover() {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<PopoverPosition>({ top: 0, left: 0, minWidth: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const gap = 4;
    const edgePadding = 8;
    const popoverHeight = popoverRef.current?.offsetHeight || 0;
    const popoverWidth = popoverRef.current?.offsetWidth || 0;

    let top: number;

    if (popoverHeight > 0) {
      // Popover is rendered, calculate intelligently
      const spaceBelow = viewportHeight - (triggerRect.bottom + gap);
      const spaceAbove = triggerRect.top - gap;

      if (spaceBelow >= popoverHeight) {
        top = triggerRect.bottom + gap;
      } else if (spaceAbove >= popoverHeight) {
        top = triggerRect.top - popoverHeight - gap;
      } else {
        // Not enough space either way, prefer bottom
        top = triggerRect.bottom + gap;
      }
    } else {
      // Popover not rendered yet, default to bottom
      top = triggerRect.bottom + gap;
    }

    // Horizontal: align to trigger's left edge; if that overflows the viewport,
    // right-align to the trigger instead so the popover stays on-screen.
    let left = triggerRect.left;
    if (popoverWidth > 0) {
      const maxLeft = viewportWidth - popoverWidth - edgePadding;
      if (left > maxLeft) left = Math.max(edgePadding, maxLeft);
    }

    setPosition({ top, left, minWidth: triggerRect.width });
  }, []);

  const open = useCallback(() => {
    updatePosition();

    setIsOpen(true);
  }, [updatePosition]);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggle = () => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      close();
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };

    const handleScroll = () => updatePosition();
    const handleResize = () => updatePosition();

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    document.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [isOpen, close, updatePosition]);

  useLayoutEffect(() => {
    if (!isOpen) return;

    // Popover is now rendered in the DOM, measure and reposition
    updatePosition();
  }, [isOpen, updatePosition]);

  return { isOpen, position, triggerRef, popoverRef, open, close, toggle };
}
